import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import infomediaService from '../../services/infomediaService';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

/* ═══════════════ helpers ═══════════════ */
const MES = ['', 'ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const CY = new Date().getFullYear();
const fmtM = (v) => (v == null || isNaN(v) ? '—' : `$${(v / 1e6).toFixed(2)} mill.`);

// Flecha + color según variación (verde sube, rojo baja).
function VarCell({ v }) {
  if (v == null || isNaN(v)) return <span className="text-muted">—</span>;
  const up = v >= 0;
  return (
    <span style={{ color: up ? '#16a34a' : '#dc2626', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {up ? '▲' : '▼'} {v.toFixed(1)} %
    </span>
  );
}

const EChart = ({ option, height = 300 }) => (
  <ReactEChartsCore echarts={echarts} option={option} style={{ height, width: '100%' }} notMerge lazyUpdate />
);

/* ═══════════════ Tabla de ranking (SOI% + Variación) ═══════════════ */
function RankTable({ title, rows }) {
  return (
    <Card className="border-0 shadow-sm h-100">
      <Card.Body className="p-0">
        <div className="px-3 pt-2 pb-1 fw-bold text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>{title}</div>
        <div className="table-responsive" style={{ maxHeight: 280, overflow: 'auto' }}>
          <table className="table table-sm table-hover mb-0 small">
            <thead className="table-light sticky-top">
              <tr>
                <th style={{ fontSize: 10 }}>{title}</th>
                <th className="text-end" style={{ fontSize: 10 }}>SOI% Año Presente</th>
                <th className="text-end" style={{ fontSize: 10 }}>Variación</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).slice(0, 100).map((r, i) => (
                <tr key={i}>
                  <td className="text-truncate" style={{ maxWidth: 170 }} title={r.nombre}>{r.nombre}</td>
                  <td className="text-end fw-semibold">{r.soiPct != null ? `${r.soiPct.toFixed(1)}%` : '—'}</td>
                  <td className="text-end"><VarCell v={r.varPct} /></td>
                </tr>
              ))}
              {(!rows || !rows.length) && <tr><td colSpan={3} className="text-center text-muted py-3">Sin datos</td></tr>}
            </tbody>
          </table>
        </div>
      </Card.Body>
    </Card>
  );
}

/* ═══════════════ Componente principal ═══════════════ */
export default function MercadoATLDashboard() {
  const [year, setYear] = useState(CY);
  const [month, setMonth] = useState(null); // null = Todos (acumulado YTD)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await infomediaService.getMercadoAtl({ year, month: month || undefined });
      setData(res);
    } catch (e) { setError(e.message || 'Error'); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const years = data?.filterOptions?.years?.length ? data.filterOptions.years : Array.from({ length: 7 }, (_, i) => CY - i);

  const lineOption = useMemo(() => {
    const serie = data?.serieMensual || [];
    const cats = serie.map((s) => MES[s.mes]);
    return {
      tooltip: { trigger: 'axis', valueFormatter: (v) => fmtM(v) },
      legend: { data: [`Inv. ${data?.yearPrev || ''}`, `Inv. ${data?.year || ''}`], bottom: 0, textStyle: { fontSize: 11 } },
      grid: { left: 55, right: 20, top: 20, bottom: 45 },
      xAxis: { type: 'category', data: cats, axisLabel: { fontSize: 11 } },
      yAxis: { type: 'value', axisLabel: { formatter: (v) => `$${(v / 1e6).toFixed(0)}M`, fontSize: 10 } },
      series: [
        { name: `Inv. ${data?.yearPrev || ''}`, type: 'line', smooth: true, data: serie.map((s) => s.invPasado), lineStyle: { width: 2 }, itemStyle: { color: '#4c1d95' }, symbolSize: 6 },
        { name: `Inv. ${data?.year || ''}`, type: 'line', smooth: true, data: serie.map((s) => s.invPresente), lineStyle: { width: 2 }, itemStyle: { color: '#06b6d4' }, symbolSize: 6 },
      ],
    };
  }, [data]);

  const exportXlsx = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const mkSheet = (rows, name) => {
      const aoa = [['Nombre', `Inv ${data.year}`, `Inv ${data.yearPrev}`, 'SOI %', 'Variación %']];
      (rows || []).forEach((r) => aoa.push([r.nombre, Math.round(r.inv), Math.round(r.invPrev), r.soiPct?.toFixed(1), r.varPct?.toFixed(1)]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name);
    };
    mkSheet(data.porMedio, 'Medios');
    mkSheet(data.porSector, 'Sectores');
    mkSheet(data.porCategoria, 'Categorias');
    mkSheet(data.porAnunciante, 'Anunciantes');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `Panorama_Mercado_ATL_${data.year}.xlsx`);
  };

  const k = data?.kpis;

  return (
    <div className="pb-4">
      {/* Header estilo MKP */}
      <div className="p-3 mb-3 rounded" style={{ background: 'linear-gradient(90deg,#2b0a3d 0%,#7a1f6b 60%,#b91c8c 100%)', color: '#fff' }}>
        <Row className="align-items-center g-2">
          <Col md={5}>
            <div className="fw-bold" style={{ fontSize: 20, letterSpacing: 1 }}>PANORAMA MERCADO PUBLICITARIO</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>ATL — TV · Internet · Radio · VP · Prensa · Revista · Suplemento (sin TV Pagada)</div>
          </Col>
          <Col md={5}>
            <div style={{ fontSize: 12 }}>
              <span style={{ opacity: 0.8 }}>PERÍODO: </span>
              <b>{month ? `${MES[month]} ${year}`.toUpperCase() : `actualizado hasta ${MES[data?.mesHasta || 12]} ${year}`.toUpperCase()}</b>
            </div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>FUENTE: INFOMEDIA NEXT</div>
          </Col>
          <Col md={2} className="text-end">
            <Button size="sm" variant="light" onClick={exportXlsx} disabled={!data} style={{ fontSize: 11 }}>⬇ Descargar Data</Button>
          </Col>
        </Row>
      </div>

      {/* Chips de años */}
      <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
        {years.map((y) => (
          <Button key={y} size="sm" variant={y === year ? 'primary' : 'outline-secondary'} onClick={() => setYear(y)} style={{ minWidth: 64, fontWeight: 600 }}>{y}</Button>
        ))}
        {loading && <Spinner animation="border" size="sm" className="ms-2" />}
      </div>
      {/* Chips de meses */}
      <div className="d-flex flex-wrap gap-1 mb-3">
        <Button size="sm" variant={month === null ? 'info' : 'outline-secondary'} onClick={() => setMonth(null)} style={{ fontSize: 11 }}>Todos</Button>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <Button key={m} size="sm" variant={month === m ? 'info' : 'outline-secondary'} onClick={() => setMonth(m)} style={{ fontSize: 11, textTransform: 'capitalize' }}>{MES[m]}.</Button>
        ))}
      </div>

      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
      {loading && !data && <div className="text-center py-5"><Spinner animation="border" /></div>}

      {data && (
        <>
          {/* KPIs + línea */}
          <Row className="g-3 mb-3">
            <Col lg={3}>
              <Card className="border-0 shadow-sm h-100"><Card.Body className="text-center">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Inv. Año Pasado: {data.yearPrev}</div>
                <div className="fw-bold" style={{ fontSize: 24, color: '#4c1d95' }}>{fmtM(k?.invPasado)}</div>
              </Card.Body></Card>
            </Col>
            <Col lg={3}>
              <Card className="border-0 shadow-sm h-100"><Card.Body className="text-center">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Inv. Año Presente: {data.year}</div>
                <div className="fw-bold" style={{ fontSize: 24, color: '#06b6d4' }}>{fmtM(k?.invPresente)}</div>
              </Card.Body></Card>
            </Col>
            <Col lg={3}>
              <Card className="border-0 shadow-sm h-100"><Card.Body className="text-center">
                <div className="text-muted text-uppercase" style={{ fontSize: 11 }}>Variación</div>
                <div className="fw-bold" style={{ fontSize: 24 }}><VarCell v={k?.varPct} /></div>
              </Card.Body></Card>
            </Col>
            <Col lg={3}>
              <Card className="border-0 shadow-sm h-100"><Card.Body className="p-2">
                <div className="text-muted text-uppercase text-center" style={{ fontSize: 10 }}>Inv. por mes ({data.yearPrev} vs {data.year})</div>
                <EChart option={lineOption} height={110} />
              </Card.Body></Card>
            </Col>
          </Row>

          {/* Línea grande */}
          <Row className="g-3 mb-3">
            <Col lg={12}>
              <Card className="border-0 shadow-sm"><Card.Body className="p-2">
                <div className="fw-bold text-uppercase mb-1" style={{ fontSize: 12 }}>Inversión año pasado y año presente por mes</div>
                <EChart option={lineOption} height={280} />
              </Card.Body></Card>
            </Col>
          </Row>

          {/* Tablas SOI% + Variación */}
          <Row className="g-3">
            <Col lg={6}><RankTable title="Medios" rows={data.porMedio} /></Col>
            <Col lg={6}><RankTable title="Sectores" rows={data.porSector} /></Col>
          </Row>
          <Row className="g-3 mt-0">
            <Col lg={6}><RankTable title="Categorías" rows={data.porCategoria} /></Col>
            <Col lg={6}><RankTable title="Anunciantes" rows={data.porAnunciante} /></Col>
          </Row>

          <div className="text-muted mt-3" style={{ fontSize: 10 }}>
            Fuente: {data.fuente}. SOI% = participación de inversión en el año presente; Variación = vs mismo período del año anterior.
          </div>
        </>
      )}
    </div>
  );
}
